'use client';

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BannerContent {
  type: 'image' | 'video';
  src: string;
  alt: string;
  title?: string;
  subtitle?: string;
}

interface BannerPubProps {
  items: BannerContent[];
  autoScrollInterval?: number;
  height?: string;
  showControls?: boolean;
  showIndicators?: boolean;
  className?: string;
}

export function BannerPub({
  items,
  autoScrollInterval = 5000,
  height = 'h-[500px]',
  showControls = true,
  showIndicators = true,
  className,
}: BannerPubProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [direction, setDirection] = useState(0);
  const [progressKey, setProgressKey] = useState(0);

  const goToNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % items.length);
    setProgressKey(prev => prev + 1);
  }, [items.length]);

  const goToPrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
    setProgressKey(prev => prev + 1);
  }, [items.length]);

  const goToSlide = useCallback((index: number) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    setProgressKey(prev => prev + 1);
  }, [currentIndex]);

  // Auto-scroll effect
  useEffect(() => {
    if (isPaused || items.length <= 1) return;

    const timer = setInterval(goToNext, autoScrollInterval);
    return () => clearInterval(timer);
  }, [isPaused, autoScrollInterval, goToNext, items.length]);

  const currentItem = items[currentIndex];

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 1.1,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: 'easeOut' as const,
      },
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.5,
        ease: 'easeIn' as const,
      },
    }),
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.3,
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl bg-foreground/5',
        height,
        className
      )}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Decorative geometric pattern overlay */}
      <div className="absolute inset-0 geometric-pattern opacity-30 pointer-events-none z-10" />

      {/* Main content */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0"
        >
          {currentItem.type === 'image' ? (
            <img
              src={currentItem.src}
              alt={currentItem.alt}
              className="w-full h-full object-cover"
            />
          ) : (
            <video
              src={currentItem.src}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="w-full h-full object-cover"
            />
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 banner-gradient-overlay" />

          {/* Content overlay */}
          {(currentItem.title || currentItem.subtitle) && (
            <motion.div
              variants={textVariants}
              initial="hidden"
              animate="visible"
              className="absolute bottom-0 left-0 right-0 p-6 md:p-10"
            >
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation controls */}

      {/* Bottom controls bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20">
        {/* Progress bar */}
        <div className="h-1 bg-primary-foreground/20">
          <motion.div
            className="h-full bg-foreground"
            initial={{ width: '0%' }}
            animate={isPaused ? { width: '0%' } : { width: '100%' }}
            transition={{
              duration: isPaused ? 0 : autoScrollInterval / 1000,
              ease: 'linear',
            }}
            key={progressKey}
          />
        </div>
      </div>
      {/* Video controls */}
      {currentItem.type === 'video' && (
        <div className="absolute top-4 right-4 z-20 flex gap-2">
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="w-10 h-10 rounded-full bg-background/20 backdrop-blur-md border border-primary-foreground/20 flex items-center justify-center text-primary-foreground hover:bg-background/40 transition-all duration-300"
            aria-label={isPaused ? 'Play' : 'Pause'}
          >
            {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="w-10 h-10 rounded-full bg-background/20 backdrop-blur-md border border-primary-foreground/20 flex items-center justify-center text-primary-foreground hover:bg-background/40 transition-all duration-300"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
      )}

      {/* Slide counter */}

    </div>
  );
}